/**
 * FacilitationPanel
 *
 * Displays real-time facilitation messages from the discourse-aware agent.
 * Receives messages via SocketIO (join_facilitation room).
 * Shown on the BYOD recording page for treatment-group sessions.
 */

import React, { useState, useEffect, useRef } from 'react';
import styles from './FacilitationPanel.module.css';
import { scopedFetch } from '../../services/scoped-fetch';

const MOVE_LABELS = {
    probe: 'Probe',
    surface_tension: 'Surface Tension',
    redirect: 'Redirect',
    invite: 'Invite',
    synthesize: 'Synthesize',
    summarize_refocus: 'Summarize',
    summary: 'Check-in',
    respond: 'Response',
};

const MOVE_COLORS = {
    probe: '#3b82f6',
    surface_tension: '#ef4444',
    redirect: '#f59e0b',
    invite: '#8b5cf6',
    synthesize: '#10b981',
    summarize_refocus: '#06b6d4',
    summary: '#64748b',
    respond: '#64748b',
};

const FacilitationPanel = ({ sessionDeviceId, socket }) => {
    const [messages, setMessages] = useState([]);
    const [collapsed, setCollapsed] = useState(false);
    const bodyRef = useRef(null);

    // Backfill: fetch existing messages on mount (covers late join, refresh, reconnect)
    useEffect(() => {
        if (!sessionDeviceId) return;
        scopedFetch(`/api/facilitation/messages/${sessionDeviceId}`)
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                if (Array.isArray(data) && data.length > 0) {
                    setMessages(data);
                }
            })
            .catch(() => {});
    }, [sessionDeviceId]);

    // Join facilitation room and listen for new messages via SocketIO
    useEffect(() => {
        if (!socket || !sessionDeviceId) return;

        socket.emit('join_facilitation', { session_device_id: sessionDeviceId });

        const handleMessage = (data) => {
            try {
                const msg = typeof data === 'string' ? JSON.parse(data) : data;
                if (msg.session_device_id && msg.session_device_id !== sessionDeviceId) return;
                setMessages(prev => {
                    const exists = prev.some(m => m.timestamp === msg.timestamp && m.message === msg.message);
                    return exists ? prev : [...prev, msg];
                });
            } catch (e) {
                console.error('Failed to parse facilitation message:', e);
            }
        };

        socket.on('facilitation_message', handleMessage);

        return () => {
            socket.off('facilitation_message', handleMessage);
            socket.emit('leave_facilitation', { session_device_id: sessionDeviceId });
        };
    }, [socket, sessionDeviceId]);

    // Auto-scroll to latest message
    useEffect(() => {
        if (bodyRef.current && !collapsed) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, collapsed]);

    const formatTime = (ts) => {
        if (!ts) return '';
        const d = new Date(ts * 1000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className={styles.panel}>
            <div
                className={styles.panelHeader}
                onClick={() => setCollapsed(c => !c)}
            >
                <span className={styles.headerIcon}>&#9672;</span>
                <span className={styles.headerTitle}>Discussion Facilitator</span>
                <span className={styles.messageCount}>
                    {messages.length > 0 ? `${messages.length} message${messages.length !== 1 ? 's' : ''}` : 'Listening...'}
                </span>
                <span className={styles.collapseIcon}>{collapsed ? '\u25B6' : '\u25BC'}</span>
            </div>

            {!collapsed && (
                <div className={styles.panelBody} ref={bodyRef}>
                    {messages.length === 0 && (
                        <div className={styles.emptyState}>
                            The facilitation agent is listening to the discussion and will offer guidance when appropriate.
                        </div>
                    )}
                    {messages.map((msg, i) => {
                        const moveType = msg.move_type || 'probe';
                        const color = MOVE_COLORS[moveType] || '#64748b';
                        const label = MOVE_LABELS[moveType] || moveType;
                        return (
                            <div key={i} className={styles.messageCard}>
                                <div className={styles.messageMeta}>
                                    <span
                                        className={styles.moveBadge}
                                        style={{ backgroundColor: color }}
                                    >
                                        {label}
                                    </span>
                                    <span className={styles.messageTime}>
                                        {formatTime(msg.timestamp)}
                                    </span>
                                </div>
                                <p className={styles.messageText}>{msg.message}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FacilitationPanel;
